import { motion, AnimatePresence } from "framer-motion";
import { VoiceRecorder } from "@/components/voice-recorder";
import { MediaUploadPanel } from "@/components/media-upload-panel";
import { useAppStore } from "@/store";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  ArrowRight,
  Zap,
  Brain,
  BookMarked,
  MessageSquare,
  Copy,
  Check,
  Wand2,
  PenTool,
  Wrench,
  ChevronRight,
  FileText,
  Save,
  X,
  Layers,
  Mail,
  Share2,
  Newspaper,
  BookOpen,
  Mic,
  Heart,
  Star,
  Feather,
  Reply,
  ExternalLink,
  Loader2,
  Pencil,
  Search,
  Upload,
  type LucideIcon,
} from "lucide-react";
import { useState, useRef, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { apiFetch } from "@/lib/api-fetch";
import { useVaguenessDetection } from "@/hooks/use-vagueness-detection";
import { NudgeHint } from "@/components/nudge-hint";
import {
  useGetVoiceProfile,
  useGetPosts,
  useGetReplyHistory,
  useCreateIdea,
} from "@workspace/api-client-react";
import { PaywallModal, usePaywallCheck } from "@/components/paywall-modal";
import { useToast } from "@/hooks/use-toast";

const pinnedPresetsKeyFor = (sid: string) => `scribpost_pinned_presets:${sid}`;

const STUDIO_PLACEHOLDERS = [
  "Write a wedding toast in my voice...",
  "Give me a 5-minute keynote opener about leadership...",
  "Draft a heartfelt birthday speech for my best friend...",
  "Write a compelling acceptance speech...",
  "Create a podcast intro that sounds like me...",
];

const GENERATOR_PLACEHOLDERS = [
  "Unpopular opinion: slow mornings are a superpower...",
  "3 things I learned from launching my first product...",
  "Hot take: most meetings should be emails...",
  "The one habit that changed how I work...",
  "Why I stopped chasing productivity and started building routines...",
];

const QUICK_REPLY_PLACEHOLDERS = [
  "Paste the message you want to reply to...",
  "Drop a comment or email here and get a smart reply...",
  "What did they say? Paste it here...",
];

function useRotatingPlaceholder(placeholders: string[], intervalMs = 4000) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % placeholders.length);
        setVisible(true);
      }, 300);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [placeholders.length, intervalMs]);

  return { placeholder: placeholders[index], visible };
}

const FORMAT_ICON_MAP: Record<string, LucideIcon> = {
  "follow-up email":      Mail,
  "cold email":           Mail,
  "email":                Mail,
  "email apology":        Mail,
  "team announcement":    Zap,
  "performance review":   FileText,
  "social media post":    Share2,
  "newsletter":           Newspaper,
  "short story":          Feather,
  "short bio":            Star,
  "brainstorm dump":      Brain,
  "interview prep":       Mic,
  "how-to guide":         BookOpen,
  "birthday message":     Heart,
  "thank you note":       Heart,
};

type PresetItem = { id: string; name: string; config: Record<string, unknown> };

function getPresetIcon(preset: PresetItem): LucideIcon {
  const format = (typeof preset.config.format === "string" ? preset.config.format : "").toLowerCase();
  return FORMAT_ICON_MAP[format] ?? Wand2;
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays}d ago`;
}

export default function Dashboard() {
  const {
    setIdea, sessionId, setPlatform, displayName,
  } = useAppStore();
  const [, setLocation] = useLocation();
  const [studioIdea, setStudioIdea] = useState("");
  const [generatorIdea, setGeneratorIdea] = useState("");
  const [copiedPostId, setCopiedPostId] = useState<string | null>(null);
  const [savedPostIds, setSavedPostIds] = useState<Set<string>>(new Set());
  const [copiedReplyId, setCopiedReplyId] = useState<string | null>(null);
  const [justTranscribed, setJustTranscribed] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const quickReplyRef = useRef<HTMLDivElement>(null);

  // Quick Reply widget state
  const [quickReplyMessage, setQuickReplyMessage] = useState("");
  const [quickReplyResult, setQuickReplyResult] = useState<string | null>(null);
  const [quickReplyLoading, setQuickReplyLoading] = useState(false);
  const [quickReplyCopied, setQuickReplyCopied] = useState(false);
  const [quickReplyError, setQuickReplyError] = useState<string | null>(null);

  // Preset state
  const [allPresets, setAllPresets] = useState<PresetItem[]>([]);
  const [pinnedPresetIds, setPinnedPresetIds] = useState<string[]>([]);
  const [showPresetCustomizer, setShowPresetCustomizer] = useState(false);
  const [presetSearch, setPresetSearch] = useState("");
  const [tempPinned, setTempPinned] = useState<string[]>([]);

  // Load pinned presets from localStorage scoped to this session
  useEffect(() => {
    const key = pinnedPresetsKeyFor(sessionId);
    try {
      const stored = localStorage.getItem(key);
      setPinnedPresetIds(stored ? (JSON.parse(stored) as string[]) : []);
    } catch {
      setPinnedPresetIds([]);
    }
  }, [sessionId]);

  // Upload expando state
  const [showUploadExpando, setShowUploadExpando] = useState(false);

  const studioPlaceholder = useRotatingPlaceholder(STUDIO_PLACEHOLDERS);
  const generatorPlaceholder = useRotatingPlaceholder(GENERATOR_PLACEHOLDERS);
  const quickReplyPlaceholder = useRotatingPlaceholder(QUICK_REPLY_PLACEHOLDERS, 5000);

  const isStudioIdeaVague = useVaguenessDetection(studioIdea);
  const isGeneratorIdeaVague = useVaguenessDetection(generatorIdea);

  const sessionHeaders = { headers: { "x-session-id": sessionId } };

  const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");

  useEffect(() => {
    apiFetch(`${baseUrl}/api/studio/presets`, { headers: { "x-session-id": sessionId } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.presets) setAllPresets(data.presets as PresetItem[]);
      })
      .catch(() => {});
  }, [sessionId, baseUrl]);

  const pinnedPresets = useMemo((): PresetItem[] => {
    if (allPresets.length === 0) return [];
    const TARGET = 8;
    let result: PresetItem[] = [];

    if (pinnedPresetIds.length > 0) {
      const map = new Map(allPresets.map(p => [p.id, p]));
      result = pinnedPresetIds.map(id => map.get(id)).filter(Boolean) as PresetItem[];
      result = result.slice(0, TARGET);
    }

    // Pad to exactly 8 with non-pinned presets
    if (result.length < TARGET) {
      const pinnedSet = new Set(result.map(p => p.id));
      const fillers = allPresets.filter(p => !pinnedSet.has(p.id));
      result = [...result, ...fillers].slice(0, TARGET);
    }

    return result;
  }, [allPresets, pinnedPresetIds]);

  const filteredPresets = useMemo(() => {
    if (!presetSearch.trim()) return allPresets;
    const q = presetSearch.toLowerCase();
    return allPresets.filter(p => p.name.toLowerCase().includes(q));
  }, [allPresets, presetSearch]);

  const { data: voiceProfile } = useGetVoiceProfile({ request: sessionHeaders });
  const { data: recentPostsData } = useGetPosts(
    { limit: 3, offset: 0 },
    { request: sessionHeaders }
  );
  const { data: replyHistoryData } = useGetReplyHistory(
    { limit: 3, offset: 0 },
    { request: sessionHeaders }
  );

  const sampleCount = voiceProfile?.sampleCount ?? 0;

  const { showPaywall, checkPaywall, dismissPaywall } = usePaywallCheck();
  const postsGeneratedCount = useAppStore((s) => s.postsGeneratedCount);
  const isSubscribed = useAppStore((s) => s.isSubscribed);
  const FREE_LIMIT = 15;

  const handleStudioOpen = () => {
    if (studioIdea.trim()) setIdea(studioIdea.trim());
    setLocation("/studio" + (studioIdea.trim() ? "?autoGenerate=1" : ""));
  };

  const handleGeneratorOpen = () => {
    if (!generatorIdea.trim()) {
      setLocation("/generator");
      return;
    }
    if (checkPaywall()) return;
    setIdea(generatorIdea.trim());
    setPlatform("twitter");
    setLocation("/generator?autoGenerate=1");
  };

  const handleOpenCustomizer = () => {
    setTempPinned(pinnedPresetIds.slice(0, 8));
    setPresetSearch("");
    setShowPresetCustomizer(true);
  };

  const handleSavePinnedPresets = (ids: string[]) => {
    setPinnedPresetIds(ids);
    try { localStorage.setItem(pinnedPresetsKeyFor(sessionId), JSON.stringify(ids)); } catch {}
    setShowPresetCustomizer(false);
  };

  const { toast } = useToast();

  const createIdeaMutation = useCreateIdea({
    mutation: {
      onSuccess: (_data, variables) => {
        const postId = (variables as { _postId?: string })._postId;
        if (postId) setSavedPostIds((prev) => new Set(prev).add(postId));
        toast({ title: "Saved to Vault!" });
      },
      onError: () => {
        toast({ title: "Failed to save", variant: "destructive" });
      },
    },
    request: sessionHeaders,
  });

  const handleSavePostToVault = (post: { id: string; content: string; platform?: string; idea?: string }) => {
    if (savedPostIds.has(post.id) || createIdeaMutation.isPending) return;
    createIdeaMutation.mutate({
      data: {
        title: (post.idea?.slice(0, 100) || post.content.slice(0, 100)) ?? "Generated post",
        notes: post.content,
        tags: post.platform ? [post.platform] : [],
        platform: post.platform,
        source: "generator",
      } as never,
      _postId: post.id,
    } as never);
  };

  const handleCopyPost = (content: string, id: string) => {
    navigator.clipboard.writeText(content);
    setCopiedPostId(id);
    setTimeout(() => setCopiedPostId(null), 2000);
  };

  const handleCopyReply = (content: string, id: string) => {
    navigator.clipboard.writeText(content);
    setCopiedReplyId(id);
    setTimeout(() => setCopiedReplyId(null), 2000);
  };

  const handleTranscriptionComplete = (text: string) => {
    setStudioIdea(text);
    setGeneratorIdea(text);
    setJustTranscribed(true);
    setTimeout(() => setJustTranscribed(false), 8000);
  };

  const handleQuickReplyGenerate = async () => {
    if (!quickReplyMessage.trim() || quickReplyLoading) return;
    setQuickReplyLoading(true);
    setQuickReplyResult(null);
    setQuickReplyError(null);
    try {
      const res = await apiFetch(`${baseUrl}/api/reply/quick`, {
        method: "POST",
        headers: { "x-session-id": sessionId, "Content-Type": "application/json" },
        body: JSON.stringify({ message: quickReplyMessage.trim(), format: "chat", tone: "friendly" }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(errData.error ?? "Failed to generate reply");
      }
      const data = await res.json() as { generatedReply?: string };
      const result = data.generatedReply ?? null;
      if (!result) throw new Error("No reply returned");
      setQuickReplyResult(result);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to generate reply";
      setQuickReplyError(msg);
    } finally {
      setQuickReplyLoading(false);
    }
  };

  const handleQuickReplyCopy = () => {
    if (!quickReplyResult) return;
    navigator.clipboard.writeText(quickReplyResult);
    setQuickReplyCopied(true);
    setTimeout(() => setQuickReplyCopied(false), 2000);
  };

  const recentPosts = recentPostsData?.posts ?? [];
  const recentReplies = replyHistoryData?.replies ?? [];

  const lastReplyDate = recentReplies[0]?.createdAt ?? null;

  const formatBadgeColor: Record<string, string> = {
    email: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25",
    chat: "bg-blue-500/15 text-blue-400 border-blue-500/25",
    formal: "bg-violet-500/15 text-violet-400 border-violet-500/25",
    casual: "bg-amber-500/15 text-amber-400 border-amber-500/25",
  };

  return (
    <>
      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes studioPulse {
          0%, 100% { opacity: 0.10; transform: scale(1); }
          50% { opacity: 0.18; transform: scale(1.06); }
        }
        .studio-glow-border {
          background: linear-gradient(135deg, hsl(188 90% 42%), hsl(188 90% 30%), hsl(188 90% 42%));
          background-size: 300% 300%;
          animation: gradientShift 5s ease infinite;
          box-shadow: 0 0 20px 4px hsl(188 90% 42% / 0.18), 0 0 48px 8px hsl(188 90% 42% / 0.08);
        }
        .hero-gradient-text {
          background: linear-gradient(90deg, hsl(188 90% 52%), hsl(205 88% 62%), hsl(265 75% 72%), hsl(205 88% 62%), hsl(188 90% 52%));
          background-size: 300% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradientShift 4s ease infinite;
        }
        .studio-ambient-bloom {
          animation: studioPulse 6s ease-in-out infinite;
        }
        .studio-inner-shimmer {
          background: hsl(222 50% 5%);
        }
        .quick-action-card:hover {
          box-shadow: 0 0 24px hsl(188 90% 42% / 0.18), 0 4px 16px hsl(188 90% 42% / 0.08);
        }
        @keyframes nudgeGlow {
          0%, 100% { box-shadow: 0 0 0 0 hsl(188 90% 42% / 0), 0 0 20px 2px hsl(188 90% 42% / 0.15); }
          50% { box-shadow: 0 0 0 2px hsl(188 90% 42% / 0.3), 0 0 28px 4px hsl(265 75% 62% / 0.2); }
        }
        .nudge-glow-card { animation: nudgeGlow 3s ease-in-out infinite; }
      `}</style>

      <PaywallModal show={showPaywall} onDismiss={dismissPaywall} />

      {/* Preset Customizer Modal */}
      <AnimatePresence>
        {showPresetCustomizer && (
          <motion.div
            key="preset-customizer-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowPresetCustomizer(false)}
            />
            <motion.div
              initial={{ scale: 0.95, y: 12 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 8 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden z-10"
            >
              {/* Modal header */}
              <div className="px-5 py-4 border-b border-border flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-foreground text-base">Customize Quick Presets</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Pick up to 8 presets to pin to your dashboard</p>
                </div>
                <button
                  onClick={() => setShowPresetCustomizer(false)}
                  className="p-1.5 rounded-lg hover:bg-secondary transition-colors ml-3 shrink-0"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>

              {/* Search */}
              <div className="p-3 border-b border-border/50">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <input
                    value={presetSearch}
                    onChange={e => setPresetSearch(e.target.value)}
                    placeholder="Search presets…"
                    className="w-full pl-9 pr-3 py-2 text-sm bg-secondary/40 border border-border rounded-xl focus:outline-none focus:ring-1 focus:ring-primary/40 placeholder:text-muted-foreground/60"
                  />
                </div>
              </div>

              {/* Count + clear */}
              <div className="px-4 py-2 flex items-center justify-between bg-secondary/20 border-b border-border/30">
                <span className="text-xs text-muted-foreground">
                  <span className={tempPinned.length >= 8 ? "text-primary font-semibold" : ""}>{tempPinned.length}</span>
                  {" / 8 selected"}
                </span>
                {tempPinned.length > 0 && (
                  <button
                    onClick={() => setTempPinned([])}
                    className="text-xs text-destructive/80 hover:text-destructive transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>

              {/* Preset list */}
              <div className="overflow-y-auto max-h-72 p-3 space-y-1">
                {filteredPresets.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No presets match your search</p>
                ) : (
                  filteredPresets.map((preset) => {
                    const PresetIcon = getPresetIcon(preset);
                    const checked = tempPinned.includes(preset.id);
                    const disabled = !checked && tempPinned.length >= 8;
                    return (
                      <button
                        key={preset.id}
                        onClick={() => {
                          if (checked) setTempPinned(p => p.filter(id => id !== preset.id));
                          else if (!disabled) setTempPinned(p => [...p, preset.id]);
                        }}
                        disabled={disabled}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-left ${
                          checked
                            ? "bg-primary/10 border border-primary/30"
                            : disabled
                            ? "opacity-35 cursor-not-allowed bg-secondary/10 border border-transparent"
                            : "bg-secondary/20 border border-transparent hover:bg-secondary/40 hover:border-border"
                        }`}
                      >
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${checked ? "bg-primary/20" : "bg-secondary/60"}`}>
                          <PresetIcon className={`w-3.5 h-3.5 ${checked ? "text-primary" : "text-muted-foreground"}`} />
                        </div>
                        <span className={`text-sm font-medium flex-1 min-w-0 truncate ${checked ? "text-primary" : "text-foreground"}`}>
                          {preset.name}
                        </span>
                        {checked && <Check className="w-4 h-4 text-primary shrink-0" />}
                      </button>
                    );
                  })
                )}
              </div>

              {/* Footer actions */}
              <div className="p-4 border-t border-border flex gap-3">
                <button
                  onClick={() => setShowPresetCustomizer(false)}
                  className="flex-1 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-secondary/60 transition-colors text-foreground"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSavePinnedPresets(tempPinned)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(265 75% 62%))" }}
                >
                  Save Presets
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-4xl mx-auto space-y-5"
      >

        {/* Training nudge — glowing card for low confidence */}
        {sampleCount < 5 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="voice-nudge-card"
          >
            {sampleCount === 0 ? (
              <div className="nudge-glow-card relative overflow-hidden rounded-2xl border border-primary/40 p-6" style={{ background: "linear-gradient(135deg, hsl(188 90% 42% / 0.10), hsl(265 75% 62% / 0.07))" }}>
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-primary/20" style={{ background: "linear-gradient(135deg, hsl(188 90% 42% / 0.25), hsl(265 75% 62% / 0.2))" }}>
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-display font-bold text-foreground text-lg leading-tight">Your voice isn't unique yet</p>
                    <p className="text-sm text-muted-foreground mt-1">Teach the AI your writing style so every post sounds unmistakably like you.</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-3 mb-5">
                  {[
                    { step: 1, label: "Add voice samples", desc: "Paste posts, emails, or just ramble", path: "/training" },
                    { step: 2, label: "Set your style", desc: "Tell us your preferred tone & format", path: "/settings#voice-preferences" },
                    { step: 3, label: "Generate your first post", desc: "Drop an idea and watch the magic", path: "/generator" },
                  ].map(({ step, label, desc, path }) => (
                    <button key={step} onClick={() => setLocation(path)} className="flex items-start gap-3 p-3.5 bg-card/60 rounded-xl border border-border hover:border-primary/40 hover:bg-card transition-all text-left group">
                      <div className="w-6 h-6 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0 text-xs font-bold text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">{step}</div>
                      <div>
                        <p className="text-sm font-semibold text-foreground leading-tight">{label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
                <Button onClick={() => setLocation("/training")} className="font-semibold shadow-lg shadow-primary/25" style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(265 75% 62%))" }}>
                  Train my voice <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            ) : (
              <div className="nudge-glow-card relative overflow-hidden rounded-2xl border border-primary/40 p-5" style={{ background: "linear-gradient(135deg, hsl(188 90% 42% / 0.08), hsl(265 75% 62% / 0.05))" }}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, hsl(188 90% 42% / 0.25), hsl(265 75% 62% / 0.2))" }}>
                    <Zap className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <p className="font-display font-bold text-foreground text-base leading-tight">
                      {sampleCount < 3 ? "Your voice isn't unique yet" : "Almost there — add a few more samples"}
                    </p>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs text-muted-foreground">
                          {sampleCount} of 5 samples · {sampleCount < 3 ? "Low confidence" : "Building confidence"}
                        </p>
                        <span className="text-xs font-semibold" style={{ color: sampleCount < 3 ? "hsl(45 90% 55%)" : "hsl(188 90% 42%)" }}>
                          {Math.round((sampleCount / 5) * 100)}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-secondary/60 rounded-full overflow-hidden">
                        <motion.div className="h-full rounded-full" style={{ background: sampleCount < 3 ? "hsl(45 90% 55%)" : "hsl(188 90% 42%)" }} initial={{ width: 0 }} animate={{ width: `${Math.min(100, (sampleCount / 5) * 100)}%` }} transition={{ duration: 0.8, ease: "easeOut" }} />
                      </div>
                    </div>
                  </div>
                  <Button size="sm" className="shrink-0 font-semibold shadow-md shadow-primary/20" style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }} onClick={() => setLocation("/training")}>
                    Train my voice <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Free post usage bar */}
        {!isSubscribed && postsGeneratedCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-2xl p-4"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Free Posts Used
              </span>
              <span className={`text-sm font-bold ${postsGeneratedCount >= FREE_LIMIT ? "text-red-400" : "text-primary"}`}>
                {postsGeneratedCount} of {FREE_LIMIT}
              </span>
            </div>
            <div className="h-2 bg-secondary/60 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{
                  background: postsGeneratedCount >= FREE_LIMIT
                    ? "hsl(0 70% 50%)"
                    : "hsl(188 90% 42%)",
                }}
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(100, (postsGeneratedCount / FREE_LIMIT) * 100)}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />
            </div>
            {postsGeneratedCount >= FREE_LIMIT && (
              <p className="text-xs text-red-400/80 mt-2">
                You've used all free posts. Subscribe to ScribPost Pro for unlimited generation.
              </p>
            )}
          </motion.div>
        )}

        {/* Hero heading — compact */}
        <div className="space-y-1">
          <h1 className="text-2xl md:text-3xl font-display font-bold leading-tight">
            {displayName ? `Hey ${displayName}, ` : ""}
            <span className="hero-gradient-text">create something.</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            Posts, emails, replies, speeches — type or speak, ScribPost writes it in your voice.
          </p>
        </div>

        {/* ── Studio + Presets Panel — 2-col row ── */}
        <div className="flex flex-col md:flex-row gap-4" data-testid="studio-presets-row">

          {/* Studio Card — left ~60% */}
          <div className="relative md:flex-[3]" data-testid="studio-card">
            <div
              className="studio-ambient-bloom absolute -inset-8 pointer-events-none rounded-full"
              style={{
                background: "radial-gradient(ellipse at 50% 50%, hsl(188 90% 42% / 0.10) 0%, transparent 65%)",
                zIndex: 0,
              }}
            />
            <div className="studio-glow-border rounded-3xl p-[2px] relative z-10 h-full">
              <div className="studio-inner-shimmer rounded-[22px] p-5 flex flex-col gap-4 h-full" data-testid="studio-card-inner">
                {/* Header */}
                <div className="flex items-start gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      background: "hsl(188 90% 42% / 0.15)",
                      border: "1px solid hsl(188 90% 42% / 0.35)",
                      boxShadow: "0 0 14px hsl(188 90% 42% / 0.20)",
                    }}
                  >
                    <Wand2 className="w-5 h-5" style={{ color: "hsl(188 90% 55%)" }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="font-display font-bold text-xl leading-tight" style={{ color: "hsl(200 14% 93%)" }}>
                        Studio
                      </h2>
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full leading-none"
                        style={{
                          background: "hsl(188 90% 42% / 0.18)",
                          color: "hsl(188 90% 65%)",
                          border: "1px solid hsl(188 90% 42% / 0.35)",
                        }}
                      >
                        PRO
                      </span>
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: "hsl(215 22% 52%)" }}>
                      Speeches, emails, pitches — long-form content in your voice.
                    </p>
                  </div>
                </div>

                {/* Textarea */}
                <div className="relative flex-1">
                  <Textarea
                    placeholder={studioPlaceholder.placeholder}
                    className="min-h-[96px] h-full resize-none text-sm rounded-xl transition-all duration-300"
                    style={{
                      background: "hsl(222 50% 8% / 0.8)",
                      borderColor: "hsl(188 90% 42% / 0.22)",
                      color: "hsl(200 14% 93%)",
                      opacity: studioIdea ? 1 : studioPlaceholder.visible ? 1 : 0.5,
                    }}
                    value={studioIdea}
                    onChange={(e) => setStudioIdea(e.target.value)}
                    data-testid="studio-textarea"
                  />
                </div>
                <NudgeHint isVague={isStudioIdeaVague} sessionDismissKey="dashboard-studio" />

                <Button
                  onClick={handleStudioOpen}
                  className="w-full h-10 rounded-xl font-semibold text-sm hover:opacity-90 transition-opacity"
                  style={{
                    background: "hsl(188 90% 42%)",
                    color: "hsl(222 50% 4%)",
                    boxShadow: "0 0 20px hsl(188 90% 42% / 0.30), 0 4px 16px hsl(188 90% 42% / 0.20)",
                  }}
                >
                  Open Studio
                  <Sparkles className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>

          {/* Quick Presets Panel — right ~40% */}
          <div
            className="md:flex-[2] flex flex-col rounded-2xl border border-border bg-card overflow-hidden"
            data-testid="preset-panel"
            style={{ minHeight: "260px" }}
          >
            {/* Panel header */}
            <div className="px-4 py-3 flex items-center justify-between border-b border-border/50 shrink-0">
              <div className="flex items-center gap-2">
                <Layers className="w-3.5 h-3.5 text-primary/70" />
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Quick Presets</span>
              </div>
              <button
                onClick={handleOpenCustomizer}
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-all text-[11px] font-medium"
                title="Customize presets"
                data-testid="customize-presets-btn"
              >
                <Pencil className="w-3 h-3" />
                Edit
              </button>
            </div>

            {/* 2×4 grid of preset buttons */}
            <div className="flex-1 p-3 overflow-y-auto">
              {pinnedPresets.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full py-6 text-center gap-2">
                  <Layers className="w-7 h-7 text-muted-foreground/25" />
                  <p className="text-xs text-muted-foreground">Loading presets…</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {pinnedPresets.map((preset) => {
                    const PresetIcon = getPresetIcon(preset);
                    return (
                      <button
                        key={preset.id}
                        data-testid={`preset-btn-${preset.id}`}
                        onClick={() => setLocation(`/studio?preset=${preset.id}`)}
                        className="flex flex-col items-start gap-1.5 p-2.5 rounded-xl border border-border/60 bg-secondary/20 hover:border-primary/40 hover:bg-primary/5 transition-all text-left group"
                      >
                        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                          <PresetIcon className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <div className="flex items-center justify-between w-full gap-1">
                          <span className="text-[11px] font-medium text-foreground leading-tight line-clamp-1 flex-1 min-w-0">
                            {preset.name}
                          </span>
                          <ChevronRight className="w-3 h-3 text-muted-foreground/40 group-hover:text-primary/50 shrink-0 transition-colors" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer link */}
            <div className="px-3 pb-3 shrink-0">
              <button
                onClick={() => setLocation("/studio")}
                className="w-full text-[11px] text-primary/60 hover:text-primary font-medium text-center py-1.5 rounded-lg hover:bg-primary/5 transition-colors border border-transparent hover:border-primary/10"
              >
                Browse all in Studio →
              </button>
            </div>
          </div>
        </div>

        {/* ── Quick Actions strip — now directly after Studio row ── */}
        <div data-testid="quick-actions-strip">
          <p className="text-[10px] font-semibold uppercase tracking-widest mb-3" style={{ color: "hsl(188 90% 42% / 0.8)" }}>Quick Actions</p>
          <div className="flex flex-wrap gap-2">
            {([
              { label: "Make This Better", action: () => setLocation("/tools?tab=smart&tool=make-this-better") },
              { label: "Text Message Fixer", action: () => setLocation("/studio?format=Text+Message+Rewriter") },
              { label: "Email / Message Reply", action: () => quickReplyRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }) },
              { label: "Marketplace Listing", action: () => setLocation("/studio?format=eBay+%2F+Marketplace+Listing+Generator") },
              { label: "Difficult Conversation", action: () => setLocation("/studio?format=Difficult+Conversation+Planner") },
              { label: "Social Post", action: () => setLocation("/studio?format=Social+media+post") },
              { label: "Resume Bullet Improver", action: () => setLocation("/studio?format=Resume+Bullet+Improver") },
              { label: "Negotiation Reply", action: () => setLocation("/studio?format=Negotiation+Response+Generator") },
            ] as Array<{ label: string; action: () => void }>).map(({ label, action }) => (
              <button
                key={label}
                data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                onClick={action}
                className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 hover:opacity-90 active:scale-95"
                style={{
                  background: "hsl(188 90% 42% / 0.12)",
                  color: "hsl(188 90% 60%)",
                  border: "1px solid hsl(188 90% 42% / 0.30)",
                  boxShadow: "0 0 10px hsl(188 90% 42% / 0.10)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "hsl(188 90% 42% / 0.22)";
                  e.currentTarget.style.borderColor = "hsl(188 90% 42% / 0.55)";
                  e.currentTarget.style.boxShadow = "0 0 18px hsl(188 90% 42% / 0.25)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "hsl(188 90% 42% / 0.12)";
                  e.currentTarget.style.borderColor = "hsl(188 90% 42% / 0.30)";
                  e.currentTarget.style.boxShadow = "0 0 10px hsl(188 90% 42% / 0.10)";
                }}
              >
                <Zap className="w-3.5 h-3.5 shrink-0" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Post Generator — compact secondary card */}
        <motion.div
          whileHover={{ boxShadow: "0 0 0 1px hsl(205 88% 52% / 0.3), 0 8px 32px hsl(205 88% 52% / 0.12)" }}
          className="bg-card border border-border rounded-2xl p-5 flex flex-col sm:flex-row sm:items-end gap-4 transition-all duration-300"
        >
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-9 h-9 rounded-xl bg-accent/15 border border-accent/20 flex items-center justify-center shrink-0">
              <PenTool className="w-4 h-4 text-accent" />
            </div>
            <div>
              <h2 className="font-display font-semibold text-base text-foreground leading-tight">
                Post Generator
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Quick posts for any platform — type or speak your idea.
              </p>
            </div>
          </div>

          <div className="flex-1 flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1 relative">
              <Textarea
                placeholder={generatorPlaceholder.placeholder}
                className="min-h-[64px] resize-none text-sm bg-secondary/40 border-border/60 focus-visible:ring-accent/30 rounded-xl transition-all duration-300"
                style={{ opacity: generatorIdea ? 1 : generatorPlaceholder.visible ? 1 : 0.5 }}
                value={generatorIdea}
                onChange={(e) => setGeneratorIdea(e.target.value)}
                data-testid="generator-textarea"
              />
              <NudgeHint isVague={isGeneratorIdeaVague} sessionDismissKey="dashboard-generator" />
            </div>
            <Button
              onClick={handleGeneratorOpen}
              variant="outline"
              className="h-10 rounded-xl font-semibold text-sm border-accent/30 hover:bg-accent/10 hover:border-accent/50 text-accent hover:text-accent transition-all shrink-0"
            >
              Generate Post
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </motion.div>

        {/* Quick Reply widget */}
        <div
          ref={quickReplyRef}
          className="relative bg-card border border-border rounded-2xl overflow-hidden"
          style={{ borderLeft: "2px solid hsl(188 90% 42% / 0.4)", boxShadow: "-3px 0 16px hsl(188 90% 42% / 0.10)" }}
          data-testid="quick-reply-widget"
        >
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, hsl(188 90% 42% / 0.2), hsl(265 75% 62% / 0.15))" }}>
                  <Reply className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground leading-none">Quick Reply</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Generate a smart reply without leaving the dashboard</p>
                </div>
              </div>
              <button
                onClick={() => setLocation("/reply")}
                className="flex items-center gap-1 text-[11px] text-primary hover:text-primary/80 font-medium transition-colors"
              >
                Advanced options <ExternalLink className="w-3 h-3" />
              </button>
            </div>

            <Textarea
              placeholder={quickReplyPlaceholder.placeholder}
              className="min-h-[80px] resize-none text-sm bg-secondary/40 border-border/60 focus-visible:ring-primary/30 rounded-xl transition-all duration-300"
              style={{ opacity: quickReplyMessage ? 1 : quickReplyPlaceholder.visible ? 1 : 0.5 }}
              value={quickReplyMessage}
              onChange={(e) => setQuickReplyMessage(e.target.value)}
              data-testid="quick-reply-input"
            />

            <Button
              onClick={handleQuickReplyGenerate}
              disabled={!quickReplyMessage.trim() || quickReplyLoading}
              className="h-10 rounded-xl font-semibold text-sm text-white shadow-md shadow-primary/20 hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(265 75% 62%))" }}
              data-testid="quick-reply-generate-btn"
            >
              {quickReplyLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  Generate Reply
                  <Sparkles className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>

            <AnimatePresence>
              {quickReplyError && (
                <motion.div
                  key="quick-reply-error"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  className="rounded-xl border border-destructive/30 p-4 bg-destructive/10"
                  data-testid="quick-reply-error"
                >
                  <p className="text-sm text-destructive leading-relaxed">{quickReplyError}</p>
                </motion.div>
              )}
              {quickReplyResult && (
                <motion.div
                  key="quick-reply-result"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  className="relative rounded-xl border border-primary/25 p-4"
                  style={{ background: "linear-gradient(135deg, hsl(188 90% 42% / 0.06), hsl(265 75% 62% / 0.04))" }}
                  data-testid="quick-reply-result"
                >
                  <p className="text-[10px] font-semibold text-primary/70 uppercase tracking-wider mb-2">Your reply</p>
                  <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{quickReplyResult}</p>
                  <button
                    onClick={handleQuickReplyCopy}
                    className="absolute top-3 right-3 p-1.5 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
                    aria-label="Copy reply"
                    data-testid="quick-reply-copy-btn"
                  >
                    {quickReplyCopied ? (
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                    ) : (
                      <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                    )}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Voice recorder strip + inline upload toggle */}
        <div className="bg-card border border-border rounded-2xl px-5 py-4" style={{ borderLeft: "2px solid hsl(188 90% 42% / 0.35)", boxShadow: "-3px 0 12px hsl(188 90% 42% / 0.08)" }}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Speak instead of type
            </p>
            <button
              onClick={() => setShowUploadExpando(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                showUploadExpando
                  ? "bg-primary/15 text-primary border border-primary/30"
                  : "bg-secondary/60 text-muted-foreground hover:text-foreground hover:bg-secondary border border-transparent"
              }`}
              data-testid="upload-toggle-btn"
            >
              <Upload className="w-3 h-3" />
              {showUploadExpando ? "Close Upload" : "Upload File"}
            </button>
          </div>
          <VoiceRecorder
            onTranscriptionComplete={handleTranscriptionComplete}
            textareaRef={textareaRef}
          />

          {/* Inline upload expando */}
          <AnimatePresence>
            {showUploadExpando && (
              <motion.div
                key="upload-expando"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <div className="mt-4 pt-4 border-t border-border/40">
                  <MediaUploadPanel
                    onSuccess={() => {
                      setShowUploadExpando(false);
                    }}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {justTranscribed && studioIdea.trim() && (
              <motion.div
                key="transcribed"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-3 flex items-center gap-2 text-xs text-primary"
              >
                <Check className="w-3.5 h-3.5 shrink-0" />
                Idea added to Studio and Post Generator above — just click to generate.
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Quick-action row — 4 nav cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            {
              icon: MessageSquare,
              label: "Smart Reply",
              sublabel: "Reply in your voice",
              path: "/reply",
              accent: "text-emerald-400",
              bg: "hover:bg-emerald-500/10 hover:border-emerald-400/40",
              badge: "NEW",
              badgeColor: "text-emerald-400 bg-emerald-500/15 border-emerald-500/30",
              lastUsed: lastReplyDate,
              cyanHover: false,
            },
            {
              icon: Wrench,
              label: "Creator Tools",
              sublabel: "Polish, rewrite, extend",
              path: "/tools",
              accent: "text-orange-400",
              bg: "hover:bg-orange-500/10 hover:border-orange-400/40",
              badge: null,
              badgeColor: "",
              lastUsed: null,
              cyanHover: false,
            },
            {
              icon: BookMarked,
              label: "Idea Vault",
              sublabel: "Browse saved posts",
              path: "/vault",
              accent: "text-accent",
              bg: "hover:bg-accent/10 hover:border-accent/40",
              badge: null,
              badgeColor: "",
              lastUsed: null,
              cyanHover: true,
            },
            {
              icon: Brain,
              label: "Voice Training",
              sublabel: "Teach the AI your style",
              path: "/training",
              accent: "text-primary",
              bg: "hover:bg-primary/10 hover:border-primary/40",
              badge: null,
              badgeColor: "",
              lastUsed: null,
              cyanHover: true,
            },
          ].map(({ icon: Icon, label, sublabel, path, accent, bg, badge, badgeColor, lastUsed, cyanHover }) => (
            <motion.button
              key={path}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setLocation(path)}
              className={`quick-action-card relative flex flex-col gap-2 p-4 rounded-2xl border border-border bg-card text-left transition-all duration-200 ${bg} group`}
            >
              {badge && !lastUsed && (
                <span className={`absolute top-3 right-3 text-[9px] font-bold border px-1.5 py-0.5 rounded-full leading-none ${badgeColor}`}>
                  {badge}
                </span>
              )}
              {lastUsed && (
                <span
                  className="absolute top-3 right-3 text-[9px] font-medium px-1.5 py-0.5 rounded-full leading-none bg-secondary/80 text-muted-foreground border border-border/60"
                  data-testid={`recency-${path.replace("/", "")}`}
                >
                  {formatTimeAgo(lastUsed)}
                </span>
              )}
              <div className="w-9 h-9 rounded-xl bg-secondary/60 flex items-center justify-center group-hover:scale-110 group-hover:shadow-[0_0_18px_hsl(188_90%_42%_/_0.28)] transition-all duration-200">
                <Icon className={`w-[18px] h-[18px] ${accent}`} />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground leading-tight">{label}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{sublabel}</p>
              </div>
            </motion.button>
          ))}
        </div>

        {/* My Posts */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" style={{ color: "hsl(188 90% 42% / 0.6)" }} />
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                My Posts
              </h3>
            </div>
            <button
              onClick={() => setLocation("/vault")}
              className="text-[11px] text-primary hover:text-primary/80 flex items-center gap-0.5 font-medium"
            >
              Open Idea Vault <ChevronRight className="w-3 h-3" />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Recent Posts */}
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-foreground/70 flex items-center gap-1.5">
                  <PenTool className="w-3.5 h-3.5" style={{ color: "hsl(188 90% 42% / 0.6)" }} /> Recent Posts
                </p>
              </div>

              {recentPosts.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border/60 p-5 text-center">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    No posts yet — generate your first one above.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentPosts.map((post) => (
                    <motion.div
                      key={post.id}
                      whileHover={{ y: -1 }}
                      className="group relative bg-card border border-border rounded-2xl p-4 hover:border-primary/30 transition-all"
                    >
                      {post.platform && (
                        <span className="inline-flex items-center text-[10px] font-semibold capitalize px-2 py-0.5 rounded-full border bg-primary/10 text-primary border-primary/20 mb-2">
                          {post.platform}
                        </span>
                      )}
                      <p className="text-xs text-foreground line-clamp-3 leading-relaxed">
                        {post.content}
                      </p>
                      <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleSavePostToVault(post)}
                          disabled={savedPostIds.has(post.id)}
                          className="p-1.5 rounded-lg bg-secondary hover:bg-secondary/80"
                          aria-label="Save to vault"
                        >
                          {savedPostIds.has(post.id) ? (
                            <Check className="w-3.5 h-3.5 text-emerald-400" />
                          ) : (
                            <Save className="w-3.5 h-3.5 text-primary" />
                          )}
                        </button>
                        <button
                          onClick={() => handleCopyPost(post.content, post.id)}
                          className="p-1.5 rounded-lg bg-secondary hover:bg-secondary/80"
                          aria-label="Copy post"
                        >
                          {copiedPostId === post.id ? (
                            <Check className="w-3.5 h-3.5 text-emerald-400" />
                          ) : (
                            <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                          )}
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Smart Replies */}
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-foreground/70 flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5" style={{ color: "hsl(188 90% 42% / 0.6)" }} /> Recent Smart Replies
                </p>
                {recentReplies.length > 0 && (
                  <button
                    onClick={() => setLocation("/reply")}
                    className="text-[11px] text-emerald-400 hover:text-emerald-400/80 flex items-center gap-0.5 font-medium"
                  >
                    View all <ChevronRight className="w-3 h-3" />
                  </button>
                )}
              </div>

              {recentReplies.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border/60 p-5 text-center">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    No replies yet — try Smart Reply to respond in your voice.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentReplies.map((reply) => (
                    <motion.div
                      key={reply.id}
                      whileHover={{ y: -1 }}
                      className="group relative bg-card border border-border rounded-2xl p-4 hover:border-emerald-400/30 transition-all"
                    >
                      <span
                        className={`inline-flex items-center text-[10px] font-semibold capitalize px-2 py-0.5 rounded-full border mb-2 ${
                          formatBadgeColor[reply.format?.toLowerCase()] ??
                          "bg-secondary/60 text-muted-foreground border-border/60"
                        }`}
                      >
                        {reply.format ?? "reply"}
                      </span>
                      <p className="text-xs text-foreground line-clamp-3 leading-relaxed">
                        {reply.generatedReply}
                      </p>
                      <button
                        onClick={() => handleCopyReply(reply.generatedReply, reply.id)}
                        className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg bg-secondary hover:bg-secondary/80"
                        aria-label="Copy reply"
                      >
                        {copiedReplyId === reply.id ? (
                          <Check className="w-3.5 h-3.5 text-emerald-400" />
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                        )}
                      </button>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>

      </motion.div>
    </>
  );
}
